<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
    <div class="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-2xl overflow-hidden">

      <!-- Header -->
      <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h4 class="text-sm font-semibold">Voicemail Greeting</h4>
        <button class="p-1 rounded-md hover:bg-gray-50" @click="$emit('close')">
          <XMarkIcon class="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <!-- Steps -->
      <div class="p-5">

        <!-- Step 1: Intro / Current greeting -->
        <div v-if="step === 'idle'" class="text-center space-y-4">
          <div class="mx-auto w-14 h-14 rounded-full bg-tvBlue/10 flex items-center justify-center">
            <MicrophoneIcon class="w-7 h-7 text-tvBlue" />
          </div>

          <div>
            <h5 class="text-sm font-semibold text-gray-800">{{ hasExisting ? 'Update Your Greeting' : 'Record Your Greeting' }}</h5>
            <p class="text-xs text-gray-500 mt-1">This is what callers hear when they reach your voicemail.</p>
          </div>

          <!-- Play existing -->
          <div v-if="hasExisting && greetingAudioUrl" class="rounded-lg border border-gray-200 p-3">
            <p class="text-[11px] text-gray-500 mb-2">Current greeting:</p>
            <audio controls class="w-full h-8" :src="greetingAudioUrl" />
          </div>

          <button class="w-full px-4 py-2.5 text-sm rounded-lg bg-tvBlue text-white hover:opacity-90 font-medium" @click="step = 'ready'">
            {{ hasExisting ? 'Record New Greeting' : 'Start Recording' }}
          </button>
        </div>

        <!-- Step 2: Ready to record -->
        <div v-else-if="step === 'ready'" class="text-center space-y-4">
          <div class="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <MicrophoneIcon class="w-7 h-7 text-red-500" />
          </div>

          <div>
            <h5 class="text-sm font-semibold text-gray-800">Ready to Record</h5>
            <p class="text-xs text-gray-500 mt-1">Click the button below and speak your greeting clearly.</p>
            <p class="text-xs text-gray-400 mt-1">Example: "Hi, you've reached John. I'm unable to take your call right now. Please leave a message and I'll get back to you."</p>
          </div>

          <button class="w-full px-4 py-2.5 text-sm rounded-lg bg-red-500 text-white hover:opacity-90 font-medium inline-flex items-center justify-center gap-2" @click="startRecording">
            <MicrophoneIcon class="w-4 h-4" /> Start Recording
          </button>
        </div>

        <!-- Step 3: Recording in progress -->
        <div v-else-if="step === 'recording'" class="text-center space-y-4">
          <div class="mx-auto w-16 h-16 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
            <MicrophoneIcon class="w-8 h-8 text-white" />
          </div>

          <div>
            <h5 class="text-sm font-semibold text-red-600">Recording...</h5>
            <p class="text-2xl font-mono font-semibold text-gray-800 mt-2">{{ formatTime(recordingSeconds) }}</p>
          </div>

          <button class="w-full px-4 py-2.5 text-sm rounded-lg bg-gray-800 text-white hover:opacity-90 font-medium" @click="stopRecording">
            Stop Recording
          </button>
        </div>

        <!-- Step 4: Preview -->
        <div v-else-if="step === 'preview'" class="text-center space-y-4">
          <div class="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <PlayCircleIcon class="w-7 h-7 text-emerald-600" />
          </div>

          <div>
            <h5 class="text-sm font-semibold text-gray-800">Preview Your Greeting</h5>
            <p class="text-xs text-gray-500 mt-1">Listen to make sure it sounds good.</p>
          </div>

          <div class="rounded-lg border border-gray-200 p-3">
            <audio controls class="w-full h-8" :src="recordedAudioUrl" />
          </div>

          <div class="flex gap-2">
            <button class="flex-1 px-4 py-2.5 text-sm rounded-lg border hover:bg-gray-50 font-medium" @click="retryRecording">
              Re-record
            </button>
            <button class="flex-1 px-4 py-2.5 text-sm rounded-lg bg-emerald-600 text-white hover:opacity-90 font-medium" :disabled="saving" @click="saveGreeting">
              {{ saving ? 'Saving...' : 'Save Greeting' }}
            </button>
          </div>
        </div>

        <!-- Step 5: Saved -->
        <div v-else-if="step === 'saved'" class="text-center space-y-4">
          <div class="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircleIcon class="w-7 h-7 text-emerald-600" />
          </div>

          <div>
            <h5 class="text-sm font-semibold text-emerald-700">Greeting Saved!</h5>
            <p class="text-xs text-gray-500 mt-1">Callers will now hear your new greeting when they reach your voicemail.</p>
          </div>

          <button class="w-full px-4 py-2.5 text-sm rounded-lg bg-gray-800 text-white hover:opacity-90 font-medium" @click="$emit('close')">
            Done
          </button>
        </div>
      </div>

      <!-- Error -->
      <div v-if="errorMsg" class="px-4 py-2 bg-red-50 text-red-700 text-xs border-t border-red-100">{{ errorMsg }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { XMarkIcon, MicrophoneIcon, PlayCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import VoicemailService from '@/services/VoicemailService'

const props = defineProps({
  hasExisting: { type: Boolean, default: false },
  greetingAudioUrl: { type: String, default: null },
  mailboxId: { type: String, default: null } // if set, uses admin endpoints instead of user endpoints
})

const emit = defineEmits(['close', 'saved'])

const step = ref('idle') // idle | ready | recording | preview | saved
const saving = ref(false)
const errorMsg = ref('')
const recordingSeconds = ref(0)
const recordedAudioUrl = ref(null)

let mediaRecorder = null
let audioChunks = []
let recordedBlob = null
let timerInterval = null

function formatTime(sec) {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

async function startRecording() {
  errorMsg.value = ''
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    audioChunks = []
    recordingSeconds.value = 0

    mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data) }
    mediaRecorder.onstop = () => {
      recordedBlob = new Blob(audioChunks, { type: 'audio/webm' })
      recordedAudioUrl.value = URL.createObjectURL(recordedBlob)
      stream.getTracks().forEach(t => t.stop())
      step.value = 'preview'
    }

    mediaRecorder.start()
    step.value = 'recording'

    timerInterval = setInterval(() => { recordingSeconds.value++ }, 1000)
  } catch (e) {
    errorMsg.value = 'Microphone access denied. Please allow microphone access in your browser settings.'
  }
}

function stopRecording() {
  if (timerInterval) { clearInterval(timerInterval); timerInterval = null }
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop()
  }
}

function retryRecording() {
  recordedAudioUrl.value = null
  recordedBlob = null
  audioChunks = []
  step.value = 'ready'
}

async function saveGreeting() {
  if (!recordedBlob) return
  saving.value = true
  errorMsg.value = ''
  try {
    const arrayBuffer = await recordedBlob.arrayBuffer()
    const audioCtx = new AudioContext({ sampleRate: 8000 })
    const decoded = await audioCtx.decodeAudioData(arrayBuffer)
    const wavBuffer = encodeWav(decoded)
    audioCtx.close()

    const base64 = btoa(String.fromCharCode(...new Uint8Array(wavBuffer)))
    const audioPayload = 'data:audio/wav;base64,' + base64
    if (props.mailboxId) {
      await VoicemailService.uploadGreetingForMailbox(props.mailboxId, audioPayload)
    } else {
      await VoicemailService.uploadGreeting(audioPayload)
    }

    step.value = 'saved'
    emit('saved')
  } catch (e) {
    errorMsg.value = 'Failed to save greeting: ' + (e?.message || 'Unknown error')
  } finally {
    saving.value = false
  }
}

function encodeWav(audioBuffer) {
  const numChannels = 1
  const sampleRate = audioBuffer.sampleRate
  const samples = audioBuffer.getChannelData(0)
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)

  function writeString(offset, str) {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * numChannels * 2, true)
  view.setUint16(32, numChannels * 2, true)
  view.setUint16(34, 16, true)
  writeString(36, 'data')
  view.setUint32(40, samples.length * 2, true)

  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
  }

  return buffer
}

onBeforeUnmount(() => {
  if (timerInterval) clearInterval(timerInterval)
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop()
  }
})
</script>
